import { ActivityLogContentItem } from '../../store/changelog/stateType';
import { UnitTypes } from '../../models/selectOptionsModels';

const propertyNameManualConversions = {
  //decision point props
  eprDate: 'EPR Date',
  invexDecisionPointId: 'Decision Point Id',
  haveBeenDuplicated: 'IS Duplicated',
  //decision sub point props
  taskIkeaId: 'Task ID',
  isCreatedByAdmin: 'Created By Admin',
  isMandatoryForAPM: 'Mandatory For APM',
  deleted: 'IS Deleted',
};

export const convertCamelCaseToHumanReadable = (text: string): string => {
  if (propertyNameManualConversions[text]) {
    return propertyNameManualConversions[text];
  }

  const result = text
    ? text
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    : '';

  return result;
};

const convertSnakeToSentence = (value: unknown): unknown => {
  if (value === null || typeof value !== 'string') {
    return value;
  }

  return value.replace(/_/g, ' ').toLowerCase();
};

const financialTypes = {
  BUDGET: 'Budget',
  FORECAST: 'Forecast',
  BASELINE: 'Baseline',
  RELEASED_APPROVED_BUDGET: 'Released and Approved Budget',
  ACTUALS: 'Actuals',
};

const commentTypes = {
  ETR_FETCH_COMMENTS: 'ETR Fetch',
  COUNTRY_PROJECT_STATUS_COMMENTS: 'Country Project Status',
  DESIGN_PROJECT_STATUS_COMMENTS: 'Design Project Status',
  CONSTRUCTION_STATUS_COMMENTS: 'Construction Status',
  UPDATE_COMMENTS: 'Updates',
  PROJECT_TIME_STATUS_COMMENTS: 'Project Time Status',
};

const responsibleTypes = {
  PROJECT_ORDERER: 'Project Orderer',
  PROJECT_OWNER: 'Project Owner',
  PROJECT_ADMIN: 'Project Admin',
  PROJECT_MANAGER: 'Project Manager',
  CPH_PLANNING_PROJECT_LEADER: 'CPH Planning Project Leader',
  CENTRES_PROJECT_MANAGER: 'Centres Project Manager',
  OFFICE_PROJECT_MANAGER: 'Office Project Manager',
  REAL_ESTATE_DEVELOPMENT_LEADER: 'Real Estate Development Leader',
  CONSTRUCTION_PROJECT_MANAGER: 'Construction Project Manager',
  DIGITAL_EXPANSION_LEADER: 'Digital Expansion Leader',
  CUSTOMER_FULFILMENT_PROJECT_LEADER: 'Customer Fulfilment Project Leader',
  COUNTRY: 'Country Project Responsible',
};

export const convertUtcToLocal = (utcString: string): string => {
  if (!utcString) return '';
  const date = new Date(utcString + 'Z');

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export const mapTitleDetails = (entry) => {
  const details = `${convertSnakeToSentence(entry.oldValue) || 'no value'} → ${convertSnakeToSentence(entry.newValue) || 'no value'}`;
  const updatedBy = `${entry.updatedByName || entry.updatedBy}`;
  let context: any = {};
  try {
    context = JSON.parse(entry.context || '{}');
  } catch {
    context = {};
  }
  let actionType = `${entry.actionType?.toLowerCase() || 'update'}d`;

  if (!entry.oldValue && entry.actionType?.toLowerCase() === 'create') {
    actionType = 'added';
  }

  if (
    entry.actionType?.toLowerCase() === 'create' &&
    entry.entityName === 'ProjectEntity' &&
    entry.propertyName === 'id'
  ) {
    return {
      title: `${updatedBy}`,
      details: 'Project created',
    };
  } else if (entry.propertyName.toLowerCase() === 'archived') {
    return {
      title: `${updatedBy}`,
      details: entry.newValue ? 'Project archived' : 'Project unarchived',
    };
  } else if (
    [
      'ProjectInvestmentEntity',
      'FinancialSummaryEntity',
      'GexAlignmentByFiscalYearEntity',
    ].includes(entry.entityName)
  ) {
    const financialType =
      entry.entityName === 'ProjectInvestmentEntity'
        ? 'Budget'
        : financialTypes[context.financialCategory] || '';
    return {
      title: `${updatedBy} ${actionType} ${context.fiscalYear} ${financialType} ${convertCamelCaseToHumanReadable(entry.propertyName)}`,
      details,
    };
  } else if (['EtrCommentEntity'].includes(entry.entityName)) {
    return {
      title: `${updatedBy} ${actionType} ${commentTypes[entry.propertyName] || convertCamelCaseToHumanReadable(entry.propertyName)} comment`,
      details,
    };
  } else if (['ProjectResponsibleEntity'].includes(entry.entityName)) {
    return {
      title: `${updatedBy} ${actionType} ${responsibleTypes[entry.propertyName] || convertCamelCaseToHumanReadable(entry.propertyName)} responsible`,
      details,
    };
  } else if (['unitType'].includes(entry.propertyName)) {
    return {
      title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)}`,
      details: `${UnitTypes[entry.oldValue] || entry.oldValue || 'no value'} → ${UnitTypes[entry.newValue] || entry.newValue || 'no value'}`,
    };
  } else if (['expansionProjectEditors.name'].includes(entry.propertyName)) {
    return {
      title: `${updatedBy} ${actionType} Confidential Users`,
      details,
    };
  } else if (entry.entityName === 'SolutionEntity') {
    return {
      title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)} [${convertSnakeToSentence(context.solutionCategory)}]`,
      details,
    };
  } else if (entry.entityName === 'DecisionPointEntity') {
    if (
      entry.actionType?.toLowerCase() === 'create' ||
      entry.actionType?.toLowerCase() === 'delete'
    ) {
      return {
        title: `${updatedBy} ${actionType} decision point ${context.decisionPoint || ''}`,
        details: '',
      };
    }
    return {
      title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)} for decision point ${context.decisionPoint || ''}`,
      details,
    };
  } else if (entry.entityName === 'DecisionSubPointEntity') {
    if (
      entry.actionType?.toLowerCase() === 'create' ||
      entry.actionType?.toLowerCase() === 'delete'
    ) {
      return {
        title: `${updatedBy} ${actionType} task ${context.taskIkeaId || ''} for decision point ${context.decisionPoint || ''}`,
        details: context.taskName ? `Task name: ${context.taskName}` : '',
      };
    }
    return {
      title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)} for decision point ${context.decisionPoint || ''} task ${context.taskIkeaId || ''}`,
      details,
    };
  } else if (entry.entityName === 'PartOfCrpEntity') {
    if (
      entry.actionType?.toLowerCase() === 'create' ||
      entry.actionType?.toLowerCase() === 'delete'
    ) {
      return {
        title: `${updatedBy} ${actionType} ${context.fiscalYear || ''} ${entry.actionType?.toLowerCase() === 'create' ? 'to' : 'from'} part of CRP`,
        details: '',
      };
    }
    if (entry.propertyName === 'approved') {
      return {
        title: `${updatedBy} ${entry.newValue === 'true' ? 'approved' : 'removed approval for'} ${context.fiscalYear || ''} part of CRP`,
        details: '',
      };
    }
    return {
      title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)} for decision point ${context.decisionPoint || ''} task ${context.taskIkeaId || ''}`,
      details,
    };
  } else if (entry.propertyName === 'boarded') {
    return {
      title: `${updatedBy} ${entry.newValue ? 'Added to' : 'Removed from'} Current Submission`,
      details: '',
    };
  }

  return {
    title: `${updatedBy} ${actionType} ${convertCamelCaseToHumanReadable(entry.propertyName)}`,
    details,
  };
};

export const convertActivityLogRawToData = (raw: ActivityLogContentItem[]) => {
  const groups: Record<
    string,
    { title: string; details: string; time: string }[]
  > = {};

  raw?.forEach((entry) => {
    const date = (entry?.updatedAt || new Date().toISOString()).slice(0, 10);
    const { title, details } = mapTitleDetails(entry);
    const time = convertUtcToLocal(entry.updatedAt);

    if (!groups[date]) groups[date] = [];
    groups[date].push({ title, details, time });
  });

  return Object.entries(groups)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, entries]) => ({
      date,
      entries,
    }));
};
